import { InfraredImage } from "@nova/protobuf"
import { generateGenericCases } from "../../generateCases"
import { Builder } from "../../sockets"
import {
    CreateInfraredImageArgs,
    getDefaultInfraredImageCreateArgs,
    UpdateInfraredImageArgs,
} from "@nova/serializer/src/client/validators/images/infrared"
import {
    addEntitySocket,
    batchAddEntitySocket,
    deleteEntitySocket,
    updateEntitySocket,
} from "features/entity-caching/project/slice"
import { createAction as createReduxAction } from "@reduxjs/toolkit"

const batchCreateInfraredImagesAction = createReduxAction<{
    entities: Pick<InfraredImage, CreateInfraredImageArgs[number]>[]
    context: { flightId: string }
}>("batchCreateInfraredImages")

const {
    create: createInfraredImage,
    update: updateInfraredImage,
    delete: deleteInfraredImage,
    createAction,
    updateAction,
    deleteAction,
} = generateGenericCases<
    InfraredImage, // entity type
    CreateInfraredImageArgs, // create args to send to server
    [
        "longitude",
        "latitude",
        "altitude",
        "height",
        "pitch",
        "yaw",
        "roll",
        "takenAt",
        "fov",
        "id",
        "make",
        "size",
        "focalLength",
        "imageHeight",
        "imageWidth",
        "imageType",
    ], // create args required by action
    UpdateInfraredImageArgs, // update args
    { flightId: string } // context
>("InfraredImage")

export const addInfraredImagesToBuilder = (builder: Builder) =>
    builder
        .addCase(createAction, (store, { payload: { data, context } }, socket) => {
            const optimisticData = InfraredImage.create({
                ...data,
                ...getDefaultInfraredImageCreateArgs(),
            })

            createInfraredImage(socket, { ...data, id: optimisticData.id }, context)
            store.dispatch(
                addEntitySocket({
                    data: optimisticData,
                    path: ["infraredFlights", { id: context.flightId }, "images"],
                })
            )
        })
        .addCase(updateAction, (store, { payload: { id, updates, context } }, socket) => {
            updateInfraredImage(socket, id, updates, context)
            store.dispatch(
                updateEntitySocket({
                    updates,
                    path: ["infraredFlights", { id: context.flightId }, "images", { id }],
                })
            )
        })
        .addCase(deleteAction, (store, { payload: { id, context } }, socket) => {
            deleteInfraredImage(socket, id, context)
            store.dispatch(
                deleteEntitySocket({
                    path: ["infraredFlights", { id: context.flightId }, "images", { id }],
                })
            )
        })
        .addCase(
            batchCreateInfraredImagesAction,
            (store, { payload: { entities, context } }, socket) => {
                const optimisticEntities = entities.map((e) =>
                    InfraredImage.create({ ...e, ...getDefaultInfraredImageCreateArgs() })
                )
                socket.emit("batchCreateInfraredImage", optimisticEntities, context)
                store.dispatch(
                    batchAddEntitySocket({
                        entities: optimisticEntities,
                        path: ["infraredFlights", { id: context.flightId }, "images"],
                    })
                )
            }
        )

export {
    createAction as createInfraredImage,
    updateAction as updateInfraredImage,
    deleteAction as deleteInfraredImage,
    batchCreateInfraredImagesAction as batchCreateInfraredImages,
}
